import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart } from './_finHelpers';

// 401K Calculator + Early Withdrawal sub-calc
export default function Retirement401kCalc() {
  return (
    <CalcShell look="cnet" title="401K Calculator" subtitle="Estimate 401(k) balance at retirement, distributions, and cost of early withdrawal."
      about={<><p>The <b>401(k)</b> is a US employer-sponsored retirement plan. Contributions are tax-deferred; employers often match a portion of employee contributions. Early withdrawals before age 59½ typically trigger a 10% penalty plus income tax.</p></>}
    >
      <Main401k/>
      <hr className="my-8 border-slate-200"/>
      <EarlyWithdrawal/>
    </CalcShell>
  );
}

function Main401k() {
  const [age, setAge] = useState(30);
  const [salary, setSalary] = useState(75000);
  const [balance, setBalance] = useState(35000);
  const [contribPct, setContribPct] = useState(10);
  const [matchPct, setMatchPct] = useState(50);
  const [matchLimit, setMatchLimit] = useState(3);
  const [retireAge, setRetireAge] = useState(65);
  const [lifeExp, setLifeExp] = useState(85);
  const [salInc, setSalInc] = useState(3);
  const [retReturn, setRetReturn] = useState(6);
  const [infl, setInfl] = useState(3);
  const c = useMemo(() => {
    const yrs = retireAge - age;
    let bal = balance;
    let sal = salary;
    const rows = [];
    for (let y = 1; y <= yrs; y++) {
      const empCont = sal * contribPct/100;
      const emplMatch = sal * Math.min(contribPct, matchLimit)/100 * matchPct/100;
      bal = bal * (1 + retReturn/100) + empCont + emplMatch;
      rows.push({ year: y, age: age+y, salary: sal, contrib: empCont, match: emplMatch, balance: bal });
      sal *= (1 + salInc/100);
    }
    const yrsInRet = lifeExp - retireAge;
    const realR = (1 + retReturn/100)/(1 + infl/100) - 1;
    const annualDist = realR === 0 ? bal / yrsInRet : bal * realR / (1 - Math.pow(1+realR, -yrsInRet));
    const monthlyDist = annualDist / 12;
    return { bal, rows, annualDist, monthlyDist };
  }, [age, salary, balance, contribPct, matchPct, matchLimit, retireAge, lifeExp, salInc, retReturn, infl]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">401K Calculator</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Basic info</h4>
          <div className="field"><label>Current age</label><input type="number" value={age} onChange={e=>setAge(+e.target.value||0)}/></div>
          <div className="field"><label>Current annual salary</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={salary} onChange={e=>setSalary(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Current 401(k) balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Contribution (% of salary)</label><div className="flex gap-2"><input type="number" step="0.01" value={contribPct} onChange={e=>setContribPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Employer match</label><div className="flex gap-2"><input type="number" step="0.01" value={matchPct} onChange={e=>setMatchPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Employer match limit</label><div className="flex gap-2"><input type="number" step="0.01" value={matchLimit} onChange={e=>setMatchLimit(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2 mt-4">Projections</h4>
          <div className="field"><label>Expected retirement age</label><input type="number" value={retireAge} onChange={e=>setRetireAge(+e.target.value||0)}/></div>
          <div className="field"><label>Life expectancy</label><input type="number" value={lifeExp} onChange={e=>setLifeExp(+e.target.value||0)}/></div>
          <div className="field"><label>Expected salary increase</label><div className="flex gap-2"><input type="number" step="0.01" value={salInc} onChange={e=>setSalInc(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% per year</span></div></div>
          <div className="field"><label>Expected annual return</label><div className="flex gap-2"><input type="number" step="0.01" value={retReturn} onChange={e=>setRetReturn(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% per year</span></div></div>
          <div className="field"><label>Expected inflation rate</label><div className="flex gap-2"><input type="number" step="0.01" value={infl} onChange={e=>setInfl(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% per year</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Balance at retirement</div><div className="result-value" data-testid="k401-balance">{fmt(c.bal)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Monthly distribution during retirement</td><td className="text-right font-semibold">{fmt(c.monthlyDist)}</td></tr>
            <tr><td>Annual distribution during retirement</td><td className="text-right">{fmt(c.annualDist)}</td></tr>
          </tbody></table>
          <div className="calc-card p-4 mt-4">
            <h4 className="font-bold text-slate-900 mb-2">Balance Growth Chart</h4>
            <BarChart
              data={c.rows.filter((_,i)=> i%Math.max(1,Math.floor(c.rows.length/10))===0 || i===c.rows.length-1).map(r => ({
                label: `${r.age}`,
                values: [
                  { value: r.balance, color: '#2563eb' },
                ]
              }))}
              stack={false}
              legend={[{ label: 'Projected 401(k) balance', color: '#2563eb' }]}
            />
          </div>
          <div className="calc-card p-4 mt-4">
            <h4 className="font-bold text-slate-900 mb-2">Growth Schedule</h4>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-2 py-1 text-left">Age</th><th className="px-2 py-1 text-right">Salary</th><th className="px-2 py-1 text-right">Contrib</th><th className="px-2 py-1 text-right">Match</th><th className="px-2 py-1 text-right">Balance</th></tr></thead>
                <tbody>
                  {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-2 py-1">{r.age}</td><td className="px-2 py-1 text-right">{fmt(r.salary)}</td><td className="px-2 py-1 text-right text-green-600">{fmt(r.contrib)}</td><td className="px-2 py-1 text-right text-blue-600">{fmt(r.match)}</td><td className="px-2 py-1 text-right font-semibold">{fmt(r.balance)}</td></tr>))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EarlyWithdrawal() {
  const [amount, setAmount] = useState(10000);
  const [fedTax, setFedTax] = useState(25);
  const [stateTax, setStateTax] = useState(5);
  const [localTax, setLocalTax] = useState(0);
  const [employed, setEmployed] = useState('yes');
  const [disability, setDisability] = useState('no');
  const [exempt, setExempt] = useState('no');
  const c = useMemo(() => {
    const penaltyApplies = employed === 'yes' && disability === 'no' && exempt === 'no';
    const penalty = penaltyApplies ? amount * 0.10 : 0;
    const totalTaxRate = (fedTax + stateTax + localTax)/100;
    const taxes = amount * totalTaxRate;
    const netReceived = amount - taxes - penalty;
    return { penalty, taxes, netReceived, totalTaxRate };
  }, [amount, fedTax, stateTax, localTax, employed, disability, exempt]);
  const Radio = ({ label, cur, setter }) => (
    <div className="flex gap-4 mt-1"><label className="flex items-center gap-1"><input type="radio" checked={cur==='yes'} onChange={()=>setter('yes')}/> Yes</label><label className="flex items-center gap-1"><input type="radio" checked={cur==='no'} onChange={()=>setter('no')}/> No</label></div>
  );
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">401(k) Early Withdrawal Costs Calculator</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Early withdrawal amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Federal income tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={fedTax} onChange={e=>setFedTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>State income tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={stateTax} onChange={e=>setStateTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Local/city income tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={localTax} onChange={e=>setLocalTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Are you employed?</label><Radio cur={employed} setter={setEmployed}/></div>
          <div className="field"><label>Do you have a qualifying disability?</label><Radio cur={disability} setter={setDisability}/></div>
          <div className="field"><label>Do you qualify for other penalty exemptions?</label><Radio cur={exempt} setter={setExempt}/></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Amount you'll receive</div><div className="result-value" data-testid="k401-early">{fmt(c.netReceived)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Withdrawal amount</td><td className="text-right">{fmt(amount)}</td></tr>
            <tr><td>Income taxes ({(c.totalTaxRate*100).toFixed(0)}%)</td><td className="text-right text-red-600">-{fmt(c.taxes)}</td></tr>
            <tr><td>10% early withdrawal penalty</td><td className="text-right text-red-600">-{fmt(c.penalty)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Net received</td><td className="text-right">{fmt(c.netReceived)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}
